import React from 'react';
import { ArrowRight, Phone, Star, MapPin, CheckCircle2, Award, Clock, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useGoogleReviews } from '../hooks/useGoogleReviews';

const Hero = () => {
  const { rating, totalReviews, loading } = useGoogleReviews();
  const whatsappNumber = "5585985195369";
  const whatsappMessage = "Olá! Gostaria de solicitar um orçamento.";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const scrollToServices = () => {
    const element = document.getElementById('servicos');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const formattedRating = rating.toFixed(1);

  return (
    <section id="inicio" className="relative bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          
          {/* LEFT COLUMN - Content & Conversion */}
          <div className="w-full lg:w-[55%] space-y-8 z-10">
            
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-blue-900 tracking-wide uppercase">
                  Gráfica Rápida e Assistência Técnica
                </span>
              </div>
              
              {!loading && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-900">
                    Empresa avaliada no Google
                  </span>
                </div>
              )}
            </div>

            {/* Main Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
              Gráfica Rápida e Assistência Técnica em Fortaleza
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Impressões, personalizados, papelaria, carimbos e assistência técnica para computadores, notebooks e impressoras. 
              <span className="font-semibold text-slate-900"> Atendimento rápido, orçamento sem compromisso e qualidade garantida.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => window.open(whatsappLink, '_blank')}
                data-testid="hero-whatsapp-btn"
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200">
                <Phone className="w-5 h-5 mr-2" />
                Solicitar Orçamento
              </Button>
              
              <Button
                onClick={scrollToServices}
                data-testid="hero-services-btn"
                size="lg"
                variant="outline"
                className="border-2 border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-200">
                Conheça nossos serviços
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-slate-200">
              <div className="flex flex-col items-center sm:items-start space-y-1">
                <CheckCircle2 className="w-5 h-5 text-green-600 mb-1" />
                <p className="text-xs sm:text-sm font-semibold text-slate-900">+15 mil</p>
                <p className="text-xs text-slate-600">Serviços realizados</p>
              </div>
              
              <div className="flex flex-col items-center sm:items-start space-y-1">
                <Award className="w-5 h-5 text-blue-600 mb-1" />
                <p className="text-xs sm:text-sm font-semibold text-slate-900">Desde 2008</p>
                <p className="text-xs text-slate-600">Atendendo Fortaleza</p>
              </div>
              
              <div className="flex flex-col items-center sm:items-start space-y-1">
                <Shield className="w-5 h-5 text-purple-600 mb-1" />
                <p className="text-xs sm:text-sm font-semibold text-slate-900">Garantia</p>
                <p className="text-xs text-slate-600">Nos serviços</p>
              </div>
              
              <div className="flex flex-col items-center sm:items-start space-y-1">
                <Clock className="w-5 h-5 text-orange-600 mb-1" />
                <p className="text-xs sm:text-sm font-semibold text-slate-900">Orçamento</p>
                <p className="text-xs text-slate-600">Rápido via WhatsApp</p>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Visual Composition with Floating Cards */}
          <div className="w-full lg:w-[45%] relative h-[450px] sm:h-[550px] lg:h-[650px] flex items-center justify-center mt-8 lg:mt-0">
            
            {/* Background Gradient Glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/50 via-transparent to-purple-100/30 rounded-3xl blur-3xl"></div>

            {/* Image Grid/Bento Composition */}
            <div className="relative w-full h-full grid grid-cols-12 grid-rows-12 gap-4">
              
              {/* Main Large Image - Printing Press */}
              <div className="col-span-8 row-span-7 rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(30,58,138,0.12)] hover:scale-[1.02] transition-transform duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1758183961426-88d64eb5f787?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBwcmludCUyMHNob3AlMjBwYXBlcnxlbnwwfHx8fDE3ODQ0MTc1OTl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Impressão profissional"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Laptop Repair Image */}
              <div className="col-span-7 row-span-5 row-start-8 rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(30,58,138,0.12)] hover:scale-[1.02] transition-transform duration-300">
                <img 
                  src="https://images.pexels.com/photos/7639374/pexels-photo-7639374.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Assistência técnica de notebooks"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Business Cards Image */}
              <div className="col-span-4 row-span-5 col-start-9 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(30,58,138,0.12)] hover:scale-[1.02] transition-transform duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1782857690704-c4341b240487?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwYnVzaW5lc3MlMjBjYXJkcyUyMHR5cG9ncmFwaHl8ZW58MHx8fHwxNzg0NDE3NTk5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Cartões de visita personalizados"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Stamps/Color Swatches Image */}
              <div className="col-span-5 row-span-4 col-start-8 row-start-6 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(30,58,138,0.12)] hover:scale-[1.02] transition-transform duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1642480532034-362360552ccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBwcmludGluZyUyMHByZXNzJTIwY29sb3JmdWwlMjBtYXRlcmlhbHN8ZW58MHx8fHwxNzg0NDE3NjUxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Materiais coloridos de impressão"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* FLOATING CARD 1 - Google Reviews */}
            <div 
              data-testid="hero-google-card"
              className="absolute top-2 left-2 sm:top-4 sm:left-4 lg:top-8 lg:left-8 bg-white/95 backdrop-blur-md border border-white/40 shadow-xl rounded-2xl p-3 sm:p-4 lg:p-5 max-w-[180px] sm:max-w-[200px] lg:max-w-[220px] hover:scale-105 transition-all duration-300"
              style={{ animation: 'float 4s ease-in-out infinite' }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                  <Star className="w-5 h-5 text-white fill-white" />
                </div>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Google Reviews
                </div>
              </div>
              {loading ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-8 bg-slate-200 rounded w-20"></div>
                  <div className="h-4 bg-slate-200 rounded w-32"></div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-4 h-4 ${
                            star <= Math.floor(rating) 
                              ? 'fill-amber-400 text-amber-400' 
                              : 'text-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-2xl font-bold text-slate-900">{formattedRating}</span>
                  </div>
                  <p className="text-xs text-slate-600">{totalReviews} avaliações verificadas</p>
                </>
              )}
            </div>

            {/* FLOATING CARD 2 - Location & Quick Info */}
            <div 
              data-testid="hero-location-card"
              className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 lg:bottom-8 lg:right-8 bg-white/95 backdrop-blur-md border border-white/40 shadow-xl rounded-2xl p-3 sm:p-4 lg:p-5 max-w-[200px] sm:max-w-[220px] lg:max-w-[240px] hover:scale-105 transition-all duration-300"
              style={{ animation: 'float 4s ease-in-out infinite 2s' }}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div className="space-y-1">
                  <p className="font-bold text-slate-900 text-sm">Fortaleza - CE</p>
                  <p className="text-xs text-slate-600">✓ Atendimento rápido</p>
                  <p className="text-xs text-slate-600">✓ Orçamento pelo WhatsApp</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Inline Keyframe Animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;